// @ts-check

import countries from '@bottlebooks/react-common/src/countries';
import { Trans } from '@lingui/macro';
import { Fragment, useState } from 'react';
import {
  Box,
  Button,
  SearchIcon,
  Table,
  TableBody,
  TableRow,
  Text,
  TextInput,
} from '~/design-system';
import useCompaniesSearch from '../../useCompaniesSearch';
import usePartnersSearch from '../../usePartnersSearch';
import Companies from './Companies';
import Partners, { BrandSkeletonRow } from './Partners';

const EMPTY = '(empty)';

/**
 * Renders the table where the suppliers are matched with brand or partner.
 * @param {Object} props
 * @param {String} props.workspaceId
 * @param {Object} props.selectedSupplier - the selected supplier
 * @param {Record<string, string>} props.suppliersMap
 * @param {({ brandId: string }) => void} props.onSelectBrand - the callback when a brand is selected
 * @param {function} props.onSelectPartner - the callback when a partner is selected
 * @param {String | undefined} props.companyId - the id of the selected company
 * @param {String | undefined} props.partnerId - the id of the selected partner
 * @param {function} [props.onAddNew] - the callback when user wants to add new partner
 * @param {"wine"|"importer"|null} [props.companyType] - the type of the company (used to filter companies/partners)
 */
export default function SelectPartner({
  workspaceId,
  selectedSupplier,
  partnerId,
  companyId,
  suppliersMap,
  onSelectBrand,
  onSelectPartner,
  onAddNew,
  companyType,
  ...rest
}) {
  const [supplierName, setSupplierName] = useState(
    /** @type {string | undefined}*/ (selectedSupplier)
  );

  const [countryCode, setCountryCode] = useState(
    /** @type {string | undefined}*/ (undefined)
  );

  const { partners: sortedPartners, loading: loadingPartners } =
    usePartnersSearch({
      workspaceId,
      searchTerm: supplierName || selectedSupplier,
      countryCode: countryCode,
      companyType,
    });
  const { companies, loading: loadingBrands } = useCompaniesSearch({
    searchTerm: supplierName,
    country: countryCode,
    companyType,
  });
  const partnersBrandIds = sortedPartners?.map(
    (partner) => partner?.brand?.brandId
  );
  // all the companies without the company already assign to partners
  const filteredCompanies = companies?.filter(
    ({ brandId }) => !partnersBrandIds.includes(brandId)
  );
  return (
    <Box {...rest}>
      <Box sx={{ textAlign: 'left', marginBottom: 3, position: 'relative' }}>
        <SearchIcon
          sx={{
            color: 'primary',
            position: 'absolute',
            top: '5px',
            left: '5px',
          }}
        />
        <TextInput
          sx={{ width: '250px', paddingLeft: '45px' }}
          autoFocus
          defaultValue={supplierName}
          onChange={(event) => {
            setSupplierName(event.target.value);
          }}
        />
        <select
          // @ts-ignore
          sx={{ marginLeft: 3, height: '24px', minWidth: '170px' }}
          onChange={({ target: { value } }) => {
            setCountryCode(value);
          }}
          defaultValue={countryCode}
        >
          <option value={EMPTY}>Country</option>
          {Object.keys(countries)?.map((countryCode) => (
            <option key={countryCode} value={countryCode}>
              {countries[countryCode]}
            </option>
          ))}
        </select>
      </Box>
      <Table sx={{ width: '100%' }}>
        {/*{sortedPartners?.length > 0 ? (*/}
        <TableBody>
          <TableRow sx={{ backgroundColor: 'brand.lightGray' }}>
            <td
              colSpan={5}
              //@ts-ignore
              sx={{ height: '32px', textAlign: 'left' }}
              {...rest}
            >
              <Text sx={{ textAlign: 'left', paddingLeft: 3 }} variant="small">
                <Trans>Partners</Trans>
              </Text>
            </td>
          </TableRow>
          {loadingPartners ? (
            <Fragment>
              <BrandSkeletonRow />
              <BrandSkeletonRow />
            </Fragment>
          ) : (
            <Partners
              partners={sortedPartners}
              suppliersMap={suppliersMap}
              selectedSupplier={selectedSupplier}
              onSelectPartner={onSelectPartner}
              partnerId={partnerId}
              companyId={companyId}
            />
          )}
          <TableRow sx={{ backgroundColor: 'brand.lightGray' }}>
            <td
              colSpan={5}
              //@ts-ignore
              sx={{ height: '32px', textAlign: 'left' }}
              {...rest}
            >
              <Text sx={{ textAlign: 'left', paddingLeft: 3 }} variant="small">
                <Trans>Other companies on Bottlebooks</Trans>
              </Text>
            </td>
          </TableRow>
          {loadingBrands ? (
            <BrandSkeletonRow />
          ) : (
            <Companies
              companies={filteredCompanies}
              suppliersMap={suppliersMap}
              selectedSupplier={selectedSupplier}
              onSelectBrand={onSelectBrand}
              partnerId={partnerId}
              companyId={companyId}
            />
          )}
        </TableBody>
      </Table>
      {onAddNew && !loadingPartners && !loadingBrands && (
        <Box>
          <Button
            variant="secondary"
            sx={{ width: '100%', marginTop: 4 }}
            onClick={() => onAddNew({ supplierName: selectedSupplier })}
          >
            Add new partner
          </Button>
        </Box>
      )}
    </Box>
  );
}
